import {
	type FrontendPlacementMessage,
	type FrontendStage,
} from '@post-office/message-maker-browser/types';

import { type ComponentType, useEffect, useState } from 'react';

import { noValidation } from '@post-office/message-maker-browser/message-helpers';

import {
	PlacementFrontend,
	type PlacementRenderProps,
} from '@post-office/browser--statuspage/statuspage-actions-spotlight-placement';

import { useMountToHtmlElement } from '@post-office/components--post-office-team';

import { usePostOfficeUserInteractions } from '@atlassian/post-office-analytics/interactions';

import { useMessageEvent } from '@post-office/message-lifecycle';

import { Text } from '@atlaskit/primitives/compiled';

import { FormattedMessage } from 'react-intl';

import { messages } from '../../../components/messages';

import { type MessageTemplateContext } from '../../../index';

import type { CreatePlacementMessageConfig } from '@post-office/message-template-contracts/placement-v2';

import { SpotlightStepCount } from '@atlaskit/spotlight/step-count';

import { SpotlightSecondaryAction } from '@atlaskit/spotlight/secondary-action';

import { SpotlightPrimaryAction } from '@atlaskit/spotlight/primary-action';

import { SpotlightHeadline } from '@atlaskit/spotlight/headline';

import { SpotlightHeader } from '@atlaskit/spotlight/header';

import { SpotlightFooter } from '@atlaskit/spotlight/footer';

import { SpotlightDismissControl } from '@atlaskit/spotlight/dismiss-control';

import { SpotlightControls } from '@atlaskit/spotlight/controls';

import { SpotlightCard } from '@atlaskit/spotlight/card';

import { SpotlightBody } from '@atlaskit/spotlight/body';

import { SpotlightActions } from '@atlaskit/spotlight/actions';

import { PopoverTarget } from '@atlaskit/spotlight/popover-target';

import { PopoverProvider } from '@atlaskit/spotlight/popover-provider';

import { PopoverContent } from '@atlaskit/spotlight/popover-content';

type RenderProps = MessageTemplateContext & PlacementRenderProps;

/**
 * DOM selectors for status-page-details to provide when integrating.
 * Step 1 targets the tabs area, step 2 targets the actions menu.
 */
export const STATUSPAGE_DETAILS_TABS_SPOTLIGHT_SELECTOR =
	'#statuspage-details-tabs-spotlight-target';

export const STATUSPAGE_DETAILS_ACTIONS_SPOTLIGHT_SELECTOR =
	'#statuspage-details-actions-spotlight-target';

const preventAutoDismiss = () => {};

interface FirstStepProps {
	dismiss: () => void | Promise<void>;
	next: () => void | Promise<void>;
}

interface LastStepProps {
	dismiss: () => void | Promise<void>;
	complete: () => void | Promise<void>;
	next: () => void | Promise<void>;
}

const SpotlightCardStep1 = ({ dismiss, next }: FirstStepProps) => (
	<SpotlightCard>
		<SpotlightHeader>
			<SpotlightHeadline>
				<FormattedMessage {...messages.tabsHeading} />
			</SpotlightHeadline>
			<SpotlightControls>
				<SpotlightDismissControl autoFocus={false} onClick={dismiss} />
			</SpotlightControls>
		</SpotlightHeader>
		<SpotlightBody>
			<div>
				<Text>
					<FormattedMessage {...messages.tabsBodyLine1} />
				</Text>
			</div>
			<div>
				<Text>
					<FormattedMessage {...messages.tabsBodyLine2} />
				</Text>
			</div>
			<div>
				<Text>
					<FormattedMessage {...messages.tabsBodyLine3} />
				</Text>
			</div>
		</SpotlightBody>
		<SpotlightFooter>
			<SpotlightStepCount>1/2</SpotlightStepCount>
			<SpotlightActions>
				<SpotlightSecondaryAction onClick={dismiss}>
					<FormattedMessage {...messages.tabsSecondaryAction} />
				</SpotlightSecondaryAction>
				<SpotlightPrimaryAction onClick={next}>
					<FormattedMessage {...messages.tabsPrimaryAction} />
				</SpotlightPrimaryAction>
			</SpotlightActions>
		</SpotlightFooter>
	</SpotlightCard>
);

const SpotlightCardStep2 = ({ dismiss, complete }: LastStepProps) => (
	<SpotlightCard>
		<SpotlightHeader>
			<SpotlightHeadline>
				<FormattedMessage {...messages.actionsHeading} />
			</SpotlightHeadline>
			<SpotlightControls>
				<SpotlightDismissControl autoFocus={false} onClick={dismiss} />
			</SpotlightControls>
		</SpotlightHeader>
		<SpotlightBody>
			<div>
				<Text>
					<FormattedMessage {...messages.actionsBody} />
				</Text>
			</div>
		</SpotlightBody>
		<SpotlightFooter>
			<SpotlightStepCount>2/2</SpotlightStepCount>
			<SpotlightActions>
				<SpotlightSecondaryAction onClick={dismiss}>
					<FormattedMessage {...messages.tabsSecondaryAction} />
				</SpotlightSecondaryAction>
				<SpotlightPrimaryAction onClick={complete}>
					<FormattedMessage {...messages.actionsGotIt} />
				</SpotlightPrimaryAction>
			</SpotlightActions>
		</SpotlightFooter>
	</SpotlightCard>
);

const tour: [
	ComponentType<Record<string, never>>,
	ComponentType<FirstStepProps>,
	ComponentType<LastStepProps>,
] = [
	() => <></>,
	({ dismiss, next }: FirstStepProps) => (
		<PopoverProvider>
			<PopoverTarget>
				<div />
			</PopoverTarget>
			<PopoverContent dismiss={preventAutoDismiss} placement="right-end" isVisible={true}>
				<SpotlightCardStep1 dismiss={dismiss} next={next} />
			</PopoverContent>
		</PopoverProvider>
	),
	({ dismiss, complete, next }: LastStepProps) => (
		<PopoverProvider>
			<PopoverTarget>
				<div />
			</PopoverTarget>
			<PopoverContent dismiss={preventAutoDismiss} placement="bottom-start" isVisible={true}>
				<SpotlightCardStep2 dismiss={dismiss} complete={complete} next={next} />
			</PopoverContent>
		</PopoverProvider>
	),
];

export const render: {
	type: 'render';
	validate: (input: unknown) => RenderProps;
	Component: (input: PlacementRenderProps & Record<string, never>) => JSX.Element | null;
} = PlacementFrontend.createFrontendStage({
	type: 'render',
	validate: noValidation<RenderProps>(),
	Component: ({ onDismiss }: RenderProps) => {
		const { messageDismissed } = useMessageEvent();
		const { onClosed, onAcknowledged, onStepChanged } = usePostOfficeUserInteractions();

		const [step, setStep] = useState<number>(1);
		const [isTabsVisible, setIsTabsVisible] = useState<boolean>(false);
		const [isActionsVisible, setIsActionsVisible] = useState<boolean>(false);

		// Poll for target DOM elements - they may not exist yet when the spotlight mounts
		useEffect(() => {
			if (typeof window === 'undefined') {
				return;
			}

			let intervalId: ReturnType<typeof setInterval> | null = null;

			const checkForElements = () => {
				const tabsElement = globalThis.document?.querySelector(
					STATUSPAGE_DETAILS_TABS_SPOTLIGHT_SELECTOR,
				);
				const actionsElement = globalThis.document?.querySelector(
					STATUSPAGE_DETAILS_ACTIONS_SPOTLIGHT_SELECTOR,
				);
				if (tabsElement) setIsTabsVisible(true);
				if (actionsElement) setIsActionsVisible(true);

				// Early exit: stop polling once both elements are found
				if (tabsElement && actionsElement && intervalId) {
					clearInterval(intervalId);
					intervalId = null;
				}
			};

			checkForElements();

			intervalId = setInterval(checkForElements, 100);
			const timeoutId = setTimeout(() => {
				if (intervalId) {
					clearInterval(intervalId);
				}
			}, 5000);

			return () => {
				if (intervalId) clearInterval(intervalId);
				clearTimeout(timeoutId);
			};
		}, []);

		const renderInlineTabs = useMountToHtmlElement({
			selectors: STATUSPAGE_DETAILS_TABS_SPOTLIGHT_SELECTOR,
			as: 'inline',
			position: 'inside',
		});

		const renderInlineActions = useMountToHtmlElement({
			selectors: STATUSPAGE_DETAILS_ACTIONS_SPOTLIGHT_SELECTOR,
			as: 'inline',
			position: 'inside',
		});

		const dismiss = async () => {
			await messageDismissed();
			void onClosed({
				interactionIdentifier: 'dismiss',
				additionalMetadata: {},
			});
			setStep(0);
			onDismiss?.();
		};

		const complete = async () => {
			await messageDismissed();
			void onAcknowledged({
				interactionIdentifier: 'got-it',
				additionalMetadata: { actionTaken: true },
			});
			setStep(0);
			onDismiss?.();
		};

		const next = () => {
			void onStepChanged({
				interactionIdentifier: 'next',
				direction: 'next',
				fromStep: step,
				toStep: Math.min(tour.length - 1, step + 1),
			});
			setStep(Math.min(tour.length - 1, step + 1));
		};

		const Step1Component = tour[1];
		const Step2Component = tour[2];

		if (!isTabsVisible || !isActionsVisible) {
			return null;
		}

		return (
			<div>
				{step === 1 && renderInlineTabs(<Step1Component dismiss={dismiss} next={next} />)}
				{step === 2 &&
					renderInlineActions(<Step2Component dismiss={dismiss} next={next} complete={complete} />)}
			</div>
		);
	},
});

export const MessageFrontend: (config: CreatePlacementMessageConfig) => FrontendPlacementMessage<{
	render: FrontendStage<PlacementRenderProps>;
}> = PlacementFrontend.createMessageFrontend({
	render,
});
